// Hot Air Balloon — a storybook balloon waiting on the meadow: a plump
// rainbow-warm striped envelope over a wicker basket, sandbags hung on the
// rim and a little burner flame licking up at the mouth. Toy-like: fat
// silhouette, oversized sandbags, warm flat colours.
//
// Frame: y = 0 is the meadow (the basket stands on it), +Z is the balloon's
// front (the side the sandbags flank). About 3.5 m tall over a 2x2 tile pad.
//
// Animation layout (flat nodes, deliberate pivots):
//   - "envelope" : the whole striped balloon + crown + mouth skirt. Sways
//                  gently about the burner point in idle, straining at the
//                  ropes. Pivot at the load ring so the mouth stays seated.
//   - "flame"    : the burner flame cones between basket and mouth. Flicker
//                  by scale pulse about the burner exit, so the flame licks
//                  upward into the envelope.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const CORAL = "#e2574c";      // envelope main — warm coral red
const AMBER = "#f7c948";      // gore stripe
const ORANGE = "#ef8a3c";     // gore stripe
const CREAM = "#f6efe3";      // gore stripe + basket rim trim
const CORAL_DARK = "#b8423a"; // crown cap + mouth skirt shading
const WICKER = "#b58045";     // basket weave — warm wicker tan
const WICKER_DARK = "#8f6231"; // basket shading bands + sandbag rope
const ROPE = "#d9c49a";       // suspension ropes
const SAND = "#cfa86a";       // sandbags
const BURNER = "#5c5350";     // burner can (warm dark, never black)
const FLAME = "#f2842c";      // outer flame
const FLAME_CORE = "#f7c948"; // hot inner core

// Frame measures.
const ENV_CY = 2.35;                        // envelope centre height
const BURNER_Y = 0.92;                      // burner exit — the flame's root
const ENVELOPE_PIVOT: [number, number, number] = [0, 1.05, 0];
const FLAME_PIVOT: [number, number, number] = [0, BURNER_Y, 0];

// One suspension rope from a basket corner up to the envelope mouth. Static —
// the envelope's idle sway is kept tiny so the ropes read as taut.
function rope(x: number, z: number): Part {
  const topX = x * 0.8, topZ = z * 0.8;
  return {
    geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.58, 6),
    color: ROPE,
    position: [(x + topX) / 2, 0.76, (z + topZ) / 2],
    rotation: [z * 0.18, 0, -x * 0.18],
  };
}

// One sandbag hung over the basket rim on a short rope loop.
function sandbag(x: number, z: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.14, 6), color: WICKER_DARK,
      position: [x, 0.44, z] },
    { geometry: new THREE.SphereGeometry(0.095, 8, 6), color: SAND,
      position: [x, 0.32, z], scale: [1, 1.3, 1] },
  ];
}

function balloonParts(): Part[] {
  const parts: Part[] = [
    // The wicker basket: a stout box with darker weave bands and a cream-less
    // wicker rim (the trim stays on the envelope's stripes).
    { geometry: new THREE.BoxGeometry(0.62, 0.46, 0.62), color: WICKER,
      position: [0, 0.28, 0] },
    { geometry: new THREE.BoxGeometry(0.64, 0.06, 0.64), color: WICKER_DARK,
      position: [0, 0.16, 0] },
    { geometry: new THREE.BoxGeometry(0.64, 0.06, 0.64), color: WICKER_DARK,
      position: [0, 0.34, 0] },
    { geometry: new THREE.BoxGeometry(0.68, 0.08, 0.68), color: WICKER_DARK,
      position: [0, 0.53, 0] },

    // Burner frame: four rods from the basket rim up to the burner can.
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.42, 6), color: BURNER,
      position: [-0.2, 0.72, -0.2], rotation: [-0.3, 0, 0.3] },
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.42, 6), color: BURNER,
      position: [0.2, 0.72, -0.2], rotation: [-0.3, 0, -0.3] },
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.42, 6), color: BURNER,
      position: [-0.2, 0.72, 0.2], rotation: [0.3, 0, 0.3] },
    { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.42, 6), color: BURNER,
      position: [0.2, 0.72, 0.2], rotation: [0.3, 0, -0.3] },
    // The burner can itself.
    { geometry: new THREE.CylinderGeometry(0.09, 0.11, 0.14, 10), color: BURNER,
      position: [0, 0.86, 0] },

    // The flame: an outer lick and a hot core, both named "flame" so they
    // pulse together about the burner exit.
    { geometry: new THREE.ConeGeometry(0.09, 0.24, 8), color: FLAME,
      position: [0, BURNER_Y + 0.12, 0], name: "flame" },
    { geometry: new THREE.ConeGeometry(0.05, 0.16, 8), color: FLAME_CORE,
      position: [0, BURNER_Y + 0.09, 0], name: "flame" },

    // Suspension ropes from the basket corners to the mouth skirt.
    rope(-0.29, -0.29), rope(0.29, -0.29), rope(-0.29, 0.29), rope(0.29, 0.29),

    // Sandbags hung on the front and side rims.
    ...sandbag(-0.38, 0.25), ...sandbag(0.38, 0.25),
    ...sandbag(-0.38, -0.25), ...sandbag(0.38, -0.25),

    // The mouth skirt: a flared throat joining the burner to the envelope.
    { geometry: new THREE.CylinderGeometry(0.56, 0.26, 0.38, 14), color: CORAL_DARK,
      position: [0, 1.18, 0], name: "envelope" },

    // The envelope: a plump coral sphere with three thin gore slabs rotated
    // about the vertical axis — each pokes just past the main surface, so the
    // balloon reads as six alternating rainbow-warm stripes.
    { geometry: new THREE.SphereGeometry(1.05, 18, 14), color: CORAL,
      position: [0, ENV_CY, 0], scale: [1, 1.15, 1], name: "envelope" },
    { geometry: new THREE.SphereGeometry(1.07, 18, 14), color: AMBER,
      position: [0, ENV_CY, 0], scale: [0.3, 1.15, 1], rotation: [0, 0, 0], name: "envelope" },
    { geometry: new THREE.SphereGeometry(1.07, 18, 14), color: ORANGE,
      position: [0, ENV_CY, 0], scale: [0.3, 1.15, 1], rotation: [0, Math.PI / 3, 0], name: "envelope" },
    { geometry: new THREE.SphereGeometry(1.07, 18, 14), color: CREAM,
      position: [0, ENV_CY, 0], scale: [0.3, 1.15, 1], rotation: [0, (2 * Math.PI) / 3, 0], name: "envelope" },

    // Crown cap on top.
    { geometry: new THREE.SphereGeometry(0.2, 10, 8), color: CORAL_DARK,
      position: [0, ENV_CY + 1.14, 0], scale: [1, 0.55, 1], name: "envelope" },
  ];
  return parts;
}

export const hotAirBalloon = defineModel({
  id: "hot-air-balloon",
  batchId: "mess",
  name: "Hot Air Balloon",
  tags: ["mess", "cute", "animated", "vehicle", "balloon", "sky", "flight", "toy"],
  summary: "A rainbow-warm striped balloon over a wicker basket, flame flickering.",
  description:
    "A storybook hot air balloon waiting on the meadow: a plump envelope of " +
    "coral, amber, orange and cream gores over a wicker basket with darker " +
    "weave bands, four taut suspension ropes, oversized sandbags hung on the " +
    "rim and a little burner flame between basket and mouth. The envelope " +
    "sways gently against its ropes in idle, and the flame flickers with a " +
    "scale pulse in the burner animation.",
  author: "models",
  footprint: 1.1,
  tiles: { w: 2, h: 2 },
  parts: balloonParts,
  animations: [
    // Idle — straining at the ropes: the whole envelope leans in a slow, tiny
    // circle about the load ring, eager to be off.
    {
      id: "idle",
      label: "Envelope sway",
      duration: 4.4,
      loop: true,
      tracks: [
        {
          target: "envelope", pivot: ENVELOPE_PIVOT,
          rotation: [
            { t: 0, value: [0.025, 0, 0], ease: "sine" },
            { t: 1.1, value: [0, 0, 0.025], ease: "sine" },
            { t: 2.2, value: [-0.025, 0, 0], ease: "sine" },
            { t: 3.3, value: [0, 0, -0.025], ease: "sine" },
            { t: 4.4, value: [0.025, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Burner flicker — the signature move: the flame licks up toward the mouth
    // in quick, uneven pulses.
    {
      id: "burn",
      label: "Burner flicker",
      duration: 0.9,
      loop: true,
      tracks: [
        {
          target: "flame", pivot: FLAME_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 0.2, value: [0.85, 1.55, 0.85], ease: "sine" },
            { t: 0.45, value: [1.1, 1.1, 1.1], ease: "sine" },
            { t: 0.65, value: [0.9, 1.4, 0.9], ease: "sine" },
            { t: 0.9, value: [1, 1, 1], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
